import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  NewTag,
  OneLineList,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import { Client } from "../types/clients.types";
import { CLIENT_STATUS_STYLES } from "../statusStyles";
import { ClientNews, newsFor } from "../clientNews";
import { clientsNewsSince } from "../openedClients";
import { useOpenedClients } from "../hooks/useOpenedClients";
import { useWaitingSubmissions } from "../hooks/useWaitingSubmissions";

// Avatar tints, picked per name so the same client always gets the same
// colour without storing one on the record.
const AVATAR_TINTS = [
  "bg-[#DDF3E4] text-[#2F8E5B]",
  "bg-[#DEEBFB] text-[#2F6BAE]",
  "bg-[#EDE7F6] text-[#5B3FA8]",
  "bg-[#FDF0DC] text-[#B4761A]",
  "bg-[#E6FAFB] text-[#00899B]",
];

const tintFor = (name: string) => {
  const sum = name
    .split("")
    .reduce((total, char) => total + char.charCodeAt(0), 0);
  return AVATAR_TINTS[sum % AVATAR_TINTS.length];
};

/**
 * The one tag a row wears, and nothing where there is nothing to say.
 *
 * One rather than two, and after the name rather than in front of it: a row
 * carrying a dot on its left and a word on its right is two marks for what a
 * practitioner reads as a single fact — there is something to do about this
 * client. So the two pieces of news share a slot and the more pressing one
 * takes it.
 *
 * Answers outrank arrival. A client who landed this morning and has already
 * sent their form back is a form to read, not a name to note: the arrival is
 * the smaller half of that, and the row still reads as new by implication.
 * Neither says anything about where the client stands — that is the Status
 * column's business — only that nobody here has dealt with them yet.
 *
 * A statement, not a control: nothing is pressed to dismiss it. The tag goes
 * because the client was opened, or because the answers were.
 */
const RowTag: React.FC<{ news: ClientNews }> = ({ news }) => {
  if (news.formWaiting)
    // The visible words are short enough to survive the column; the label is
    // what the whole of it is called when it is read out.
    return (
      <NewTag label="Pre-consultation form received">Form received</NewTag>
    );

  return news.unopened ? <NewTag>New</NewTag> : null;
};

// Activity is how long the client's plan runs for, so it only says anything
// while one is running. The Old tab is archived clients — nothing to measure —
// so it drops the column and the other four share the width back.
const COLUMNS_WITH_ACTIVITY = [
  { heading: "Client name", width: "w-[32%]" },
  { heading: "Goal", width: "w-[20%]" },
  { heading: "Activity", width: "w-[14%]" },
  { heading: "Joined on", width: "w-[18%]" },
  { heading: "Status", width: "w-[16%]" },
];

const COLUMNS_WITHOUT_ACTIVITY = [
  { heading: "Client name", width: "w-[34%]" },
  { heading: "Goal", width: "w-[24%]" },
  { heading: "Joined on", width: "w-[22%]" },
  { heading: "Status", width: "w-[20%]" },
];

interface ClientsTableProps {
  clients: Client[];
  /** Stands in for the table body when the list comes back with no rows. */
  empty: EmptyStateProps;
  /**
   * Where the list is. While it is on its way the body says nothing — no rows
   * and no message — and only a load slow enough to notice shimmers, as one
   * block the size of the table. Rows already in hand are left alone either
   * way: a table with something to show goes on showing it while the next
   * answer is fetched.
   */
  phase?: LoadPhase;
  /** Off for archived clients, who have no plan running to have a length. */
  showActivity?: boolean;
  onSelectClient: (client: Client) => void;
}

const ClientsTable: React.FC<ClientsTableProps> = ({
  clients,
  empty,
  phase = "ready",
  showActivity = true,
  onSelectClient,
}) => {
  const columns = showActivity
    ? COLUMNS_WITH_ACTIVITY
    : COLUMNS_WITHOUT_ACTIVITY;

  // What this browser has already been shown, and the line it started counting
  // from — the two halves of what a row's flags mean. Read once for the table
  // rather than per row: ten rows subscribing to one module store would move
  // together anyway, and the set changing is a repaint of the list either way.
  // See `clientNews`.
  const seen = useOpenedClients();
  const since = clientsNewsSince();
  // Answers that have come back, which the rows cannot learn from the list
  // they were read off — see `useWaitingSubmissions`.
  const waiting = useWaitingSubmissions();

  return phase !== "ready" && clients.length === 0 ? (
    // The table's own area, standing in for the whole list — see
    // `TABLE_BODY_SKELETON`. Hidden from screen readers with the rest of the
    // placeholders; the card above says it is loading in words.
    <div className="flex-1 min-h-0 p-4 sm:p-6">
      <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
    </div>
  ) : (
  // The only scroll region on the page: rows move, the sticky header row and
  // the card's pagination footer stay put. `min-h-0` is what lets this shrink
  // inside the card's flex column instead of pushing the footer off-screen.
  <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
    {/* Full height only when the rows are gone: an empty table that stops at
        its header leaves the state stranded at the top of the card with a
        screenful of nothing under it, so the body takes the whole scroll
        region and the one cell in it centres in that. With rows to show the
        table sizes to them, or every row would stretch to share the height. */}
    <table
      className={`w-full table-fixed border-collapse text-left ${
        showActivity ? "min-w-[760px]" : "min-w-[640px]"
      } ${clients.length === 0 ? "h-full" : ""}`}
    >
      <colgroup>
        {columns.map((column) => (
          <col key={column.heading} className={column.width} />
        ))}
      </colgroup>
      {/* The header pins to the top of this container. Its bottom rule is an
          inset shadow rather than a border because collapsed table borders
          scroll away from a sticky cell. */}
      <thead className="sticky top-0 z-10">
        <tr>
          {columns.map((column) => (
            <th
              key={column.heading}
              scope="col"
              className="bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
            >
              {column.heading}
            </th>
          ))}
        </tr>
      </thead>
      {clients.length === 0 ? (
        <tbody>
          <tr>
            <td colSpan={columns.length} className="align-middle">
              <EmptyState {...empty} />
            </td>
          </tr>
        </tbody>
      ) : (
        <tbody>
          {clients.map((client) => {
            const news: ClientNews = newsFor(client, seen, since, waiting);

            return (
              <tr
                key={client.id}
                onClick={() => onSelectClient(client)}
                className="border-b border-line last:border-0 hover:bg-[#F8FEFF] transition-colors cursor-pointer"
              >
                <td className="px-4 sm:px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className={`w-8 h-8 3xl:w-9 3xl:h-9 rounded-full flex items-center justify-center flex-shrink-0 ${tintFor(
                          client.name
                        )}`}
                      >
                        <span className="text-[13px] font-medium">
                          {client.name.charAt(0)}
                        </span>
                      </span>
                      {/* The whole row is clickable for pointers, but the name is
                          a real button so the list stays reachable by keyboard. */}
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onSelectClient(client);
                        }}
                        className="text-[14px] text-ink font-medium truncate text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded"
                      >
                        {client.name}
                      </button>
                      {/* Keeps its width while the name truncates — a tag
                          clipped to "Form rece" is worse than a shorter name. */}
                      <RowTag news={news} />
                    </div>
                  </div>
                </td>
                {/* The same one-line-plus-count reading the Details tab gives a
                    client's goals: whatever fits stays on the line, the rest
                    becomes a "+N" that opens the full list on hover — see
                    `OneLineList`. The cell is a fixed share of a `table-fixed`
                    table, so that is almost always one name here. */}
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink">
                  <OneLineList items={client.goals} label="goals" />
                </td>
                {showActivity && (
                  <td className="px-4 sm:px-6 py-4 text-[14px] text-ink">
                    {client.activity}
                  </td>
                )}
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink">
                  {client.joinedOn}
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <span
                    className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                      CLIENT_STATUS_STYLES[client.status]
                    }`}
                  >
                    {client.status}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      )}
    </table>
  </div>
  );
};

export default ClientsTable;
